import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getPublicContactSettings,
  FALLBACK_CONTACT_SETTINGS,
  type ContactSettings,
} from '../features/contactSettings';
import { getTodaySalaahTimes, type SalaahTimings } from '../features/salaahTimes';
import { parseSocialLinksJson, socialPlatformMeta } from '../shared/lib';
import bmcsLogo from '../assets/images/logo/bmcs-transparent.png';

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/masjid', label: 'Masjid' },
  { to: '/classes', label: 'Classes' },
  { to: '/contact', label: 'Contact' },
] as const;

const SALAAH_ORDER: Array<{ key: keyof SalaahTimings; label: string }> = [
  { key: 'fajr', label: 'Fajr' },
  { key: 'dhuhr', label: 'Dhuhr' },
  { key: 'asr', label: 'Asr' },
  { key: 'maghrib', label: 'Maghrib' },
  { key: 'isha', label: 'Isha' },
];

function parseMinutes(time: string): number {
  const [h, m] = time.split(':').map((part) => Number(part));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return -1;
  return h * 60 + m;
}

function getNextSalaahLabel(timings: SalaahTimings): string {
  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  for (const prayer of SALAAH_ORDER) {
    const minutes = parseMinutes(timings[prayer.key]);
    if (minutes > current) return prayer.label;
  }
  return 'Fajr';
}

function whatsappHref(number: string): string {
  const digits = number.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
}

const Footer: React.FC = () => {
  const [settings, setSettings] = useState<ContactSettings>(FALLBACK_CONTACT_SETTINGS);
  const [timings, setTimings] = useState<SalaahTimings | null>(null);
  const mapsQuery = useMemo(() => encodeURIComponent(settings.address), [settings.address]);
  const footerLinks = useMemo(
    () => parseSocialLinksJson(settings.footerLinksJson),
    [settings.footerLinksJson]
  );
  const currentYear = new Date().getFullYear();
  const nextSalaah = timings ? getNextSalaahLabel(timings) : null;

  useEffect(() => {
    getPublicContactSettings()
      .then(setSettings)
      .catch(() => setSettings(FALLBACK_CONTACT_SETTINGS));
  }, []);

  useEffect(() => {
    getTodaySalaahTimes()
      .then((data) => setTimings(data.timings))
      .catch(() => setTimings(null));
  }, []);

  return (
    <footer className="footer-v2">
      <div className="container footer-v2__inner">
        <div className="row g-4 g-xl-4 align-items-start">
          {/* Brand */}
          <div className="col-12 col-md-6 col-xl-3 footer-v2-col">
            <div className="footer-v2-brand-row">
              <img src={bmcsLogo} alt="" className="footer-v2-logo" />
              <h2 className="footer-v2-brand">
                Bothasig Muslim and Cultural Society
              </h2>
            </div>
            <p className="footer-v2-tagline">Serving the community since 2000.</p>
            {footerLinks.length > 0 ? (
              <div className="footer-v2-follow">
                <p className="footer-v2-follow-label">Follow us</p>
                <div className="footer-v2-socials">
                  {footerLinks.map((link, idx) => {
                    const meta = socialPlatformMeta[link.platform] ?? socialPlatformMeta.custom;
                    return (
                      <a
                        key={`${link.url}-${idx}`}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-v2-social"
                        aria-label={link.label || meta.title}
                        title={link.label || meta.title}
                      >
                        <i className={`bi ${meta.icon}`} aria-hidden />
                      </a>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>

          {/* Quick links */}
          <div className="col-6 col-md-6 col-xl-2 footer-v2-col">
            <h3 className="footer-v2-heading">Quick Links</h3>
            <ul className="footer-v2-links list-unstyled mb-0">
              {QUICK_LINKS.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="footer-v2-nav-link">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Location */}
          <div className="col-6 col-md-6 col-xl-2 footer-v2-col">
            <h3 className="footer-v2-heading">Our Location</h3>
            <div className="footer-v2-list-item">
              <i className="bi bi-geo-alt-fill footer-v2-icon" aria-hidden />
              <div>
                <p className="footer-v2-address mb-2">{settings.address}</p>
                <a
                  href={`https://maps.google.com/?q=${mapsQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-v2-map-btn"
                >
                  <i className="bi bi-pin-map-fill" aria-hidden />
                  View on Map
                </a>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="col-12 col-md-6 col-xl-2 footer-v2-col">
            <h3 className="footer-v2-heading">Quick Contact</h3>
            <ul className="footer-v2-contact-list list-unstyled mb-0">
              <li>
                <a
                  href={whatsappHref(settings.whatsappNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-v2-list-item footer-v2-list-item--link"
                >
                  <i className="bi bi-whatsapp footer-v2-icon" aria-hidden />
                  <span>
                    <span className="footer-v2-contact-label">WhatsApp</span>
                    <span className="footer-v2-contact-value">{settings.whatsappNumber}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${settings.emailAddress}`}
                  className="footer-v2-list-item footer-v2-list-item--link"
                >
                  <i className="bi bi-envelope-fill footer-v2-icon" aria-hidden />
                  <span>
                    <span className="footer-v2-contact-label">Email</span>
                    <span className="footer-v2-contact-value text-break">{settings.emailAddress}</span>
                  </span>
                </a>
              </li>
            </ul>
          </div>

          {/* Salaah */}
          <div className="col-12 col-md-6 col-xl-3 footer-v2-col">
            <h3 className="footer-v2-heading">Daily Salaah</h3>
            {timings ? (
              <ul className="footer-v2-salaah list-unstyled mb-0">
                {SALAAH_ORDER.map((prayer) => {
                  const isNext = nextSalaah === prayer.label;
                  return (
                    <li
                      key={prayer.key}
                      className={`footer-v2-salaah__row${isNext ? ' is-next' : ''}`}
                    >
                      <span className="footer-v2-salaah__name">
                        {prayer.label}
                        {isNext ? <span className="footer-v2-salaah__badge">Next</span> : null}
                      </span>
                      <span className="footer-v2-salaah__time">{timings[prayer.key]}</span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="footer-v2-salaah-fallback mb-0">
                Today&apos;s times will appear here shortly.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="footer-v2-bottom">
        <div className="container footer-v2-bottom__inner">
          <p className="footer-v2-bottom__copy mb-0">
            © {currentYear} Bothasig Muslim and Cultural Society. All rights reserved.
          </p>
          <div className="footer-v2-bottom__meta">
            <Link to="/privacy" className="footer-v2-link">
              Privacy &amp; Data Policy
            </Link>
            <span className="footer-v2-bottom__sep" aria-hidden="true">
              ·
            </span>
            <span className="footer-v2-bottom__powered">
              Powered by{' '}
              <a
                href="https://indexless.co.za"
                target="_blank"
                rel="noreferrer"
                className="footer-v2-link"
              >
                Indexless
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
